import React, { useState } from "react";
import { ListGroup, ListGroupItem, Badge, Collapse } from "reactstrap";
import * as FeatherIcons from "react-feather";
import { ChevronDown, ChevronRight } from "react-feather";

/**
 * ModuleViewSelector - Left sidebar for view selection with tree structure
 *
 * @param {Object} props
 * @param {Array} props.views - Array of category objects with subViews
 * @param {string} props.activeView - Currently active view ID
 * @param {Function} props.onViewChange - Callback when view is changed
 */
const ModuleViewSelector = ({ views = [], activeView, onViewChange }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const getIcon = (iconName) => {
    const Icon = FeatherIcons[iconName];
    return Icon ? <Icon size={18} /> : null;
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  // Initialize expanded state based on active view
  React.useEffect(() => {
    if (activeView && views.length > 0) {
      const categoryWithActiveView = views.find((category) =>
        category.subViews?.some((subView) => subView.id === activeView)
      );
      if (categoryWithActiveView && !expandedCategories[categoryWithActiveView.category]) {
        setExpandedCategories((prev) => ({
          ...prev,
          [categoryWithActiveView.category]: true,
        }));
      }
    }
  }, [activeView, views]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle both flat views (old structure) and categorized views (new structure)
  const isCategorizedStructure = views.length > 0 && views[0].category !== undefined;

  if (!isCategorizedStructure) {
    // Render old flat structure for backward compatibility
    return (
      <div className="module-view-selector">
        <h5 className="mb-3">Views</h5>
        <ListGroup>
          {views.map((view) => (
            <ListGroupItem
              key={view.id}
              active={activeView === view.id}
              onClick={() => onViewChange(view.id)}
              className="d-flex align-items-center cursor-pointer"
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex align-items-center w-100">
                {view.icon && (
                  <span className="me-3">
                    {getIcon(view.icon)}
                  </span>
                )}
                <div className="flex-grow-1">
                  <h6 className="mb-0">{view.name}</h6>
                  {view.description && (
                    <small className="text-muted d-block">{view.description}</small>
                  )}
                </div>
                {view.count !== undefined && (
                  <Badge color="primary" pill>
                    {view.count}
                  </Badge>
                )}
              </div>
            </ListGroupItem>
          ))}
        </ListGroup>

        {views.length === 0 && (
          <div className="text-center text-muted py-4">
            <p>No views available</p>
          </div>
        )}
      </div>
    );
  }

  // Render new tree structure
  return (
    <div className="module-view-selector">
      <h5 className="mb-3">Views</h5>
      <ListGroup>
        {views.map((category) => {
          const isExpanded = expandedCategories[category.category] || false;
          const hasSubViews = category.subViews && category.subViews.length > 0;

          return (
            <div key={category.category}>
              {/* Category Header */}
              <ListGroupItem
                onClick={() => hasSubViews && toggleCategory(category.category)}
                className="d-flex align-items-center cursor-pointer category-header"
                style={{
                  cursor: hasSubViews ? "pointer" : "default",
                  backgroundColor: "#f8f9fa",
                  fontWeight: "600",
                }}
              >
                <div className="d-flex align-items-center w-100">
                  {hasSubViews && (
                    <span className="me-2">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                  )}
                  {category.icon && (
                    <span className="me-2">
                      {getIcon(category.icon)}
                    </span>
                  )}
                  <div className="flex-grow-1">
                    <span>{category.category}</span>
                  </div>
                  
                </div>
              </ListGroupItem>

              {/* Sub-views */}
              {hasSubViews && (
                <Collapse isOpen={isExpanded}>
                  <ListGroup flush>
                    {category.subViews.map((subView) => (
                      <ListGroupItem
                        key={subView.id}
                        active={activeView === subView.id}
                        onClick={() => onViewChange(subView.id)}
                        className="d-flex align-items-center cursor-pointer sub-view-item"
                        style={{
                          cursor: "pointer",
                          paddingLeft: "3rem",
                          borderLeft: activeView === subView.id ? "3px solid var(--theme-default)" : "3px solid transparent",
                        }}
                      >
                        <div className="d-flex align-items-center w-100">
                          {subView.icon && (
                            <span className="me-2">
                              {getIcon(subView.icon)}
                            </span>
                          )}
                          <div className="flex-grow-1">
                            <h6 className="mb-0" style={{ fontSize: "0.9rem" }}>
                              {subView.name}
                            </h6>
                            {subView.description && (
                              <small className="text-muted d-block">{subView.description}</small>
                            )}
                          </div>
                          {subView.count !== undefined && (
                            <Badge color="primary" pill>
                              {subView.count}
                            </Badge>
                          )}
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </Collapse>
              )}
            </div>
          );
        })}
      </ListGroup>

      {views.length === 0 && (
        <div className="text-center text-muted py-4">
          <p>No views available</p>
        </div>
      )}
    </div>
  );
};

export default ModuleViewSelector;
